/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CardNoteProps {
  id: string;
  content: string;
  noteTime: Date;
  authorId: string | null;
  isInternal: boolean;
  noteCategory: string | null;
  noteMetadataJson: string | null;
  isSystem: boolean;
  sentToCards: List<IdListItemProps> | null;
  receivedFromCardId: string | null;
  cardId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CardNoteRecord extends Record<CardNoteProps>({
  id: null as any,
  content: null as any,
  noteTime: null as any,
  authorId: null as any,
  isInternal: null as any,
  noteCategory: null as any,
  noteMetadataJson: null as any,
  isSystem: null as any,
  sentToCards: null as any,
  receivedFromCardId: null as any,
  cardId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    isInternal: boolean;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    isSystem: boolean;
    sentToCards: any;
    receivedFromCardId: string | null;
    cardId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CardNoteRecord {
    if (json == null) {
      throw new Error('CardNoteRecord.fromJS: parameter cannot be null');
    }
    return new CardNoteRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CardNoteJson.id',
      ),
      content: getFromOrThrow(json, (j) => j.content, 'CardNoteJson.content'),
      noteTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
          'CardNoteJson.noteTime',
        ),
      ),
      authorId: json.authorId,
      isInternal: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isInternal', false),
        'CardNoteJson.isInternal',
      ),
      noteCategory: json.noteCategory,
      noteMetadataJson: json.noteMetadataJson,
      isSystem: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSystem', false),
        'CardNoteJson.isSystem',
      ),
      sentToCards:
        json.sentToCards != null
          ? List<IdListItemRecord>(json.sentToCards.map((o: any) => new IdListItemRecord(o)))
          : null,
      receivedFromCardId: json.receivedFromCardId,
      cardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
        'CardNoteJson.cardId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'CardNoteJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    isInternal: boolean;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    isSystem: boolean;
    sentToCards: any;
    receivedFromCardId: string | null;
    cardId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CardNoteRecord, Error[]] {
    if (json == null) {
      throw new Error('CardNoteRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CardNoteRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CardNoteJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.content, 'CardNoteJson.content'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
              'CardNoteJson.noteTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () => json.authorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isInternal: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isInternal', false),
            'CardNoteJson.isInternal',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteCategory: tryOrDefault(
        () => json.noteCategory,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteMetadataJson: tryOrDefault(
        () => json.noteMetadataJson,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSystem: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isSystem', false),
            'CardNoteJson.isSystem',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sentToCards: tryOrDefault(
        () =>
          json.sentToCards != null
            ? List<IdListItemRecord>(json.sentToCards.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      receivedFromCardId: tryOrDefault(
        () => json.receivedFromCardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
            'CardNoteJson.cardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'CardNoteJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CardNoteProps) {
    super(props);
    if (props == null) {
      throw new Error('CardNoteRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CardNoteViewModelBase implements CardNoteProps {
  protected readonly _props: CardNoteProps;
  private _json?: CardNoteProps;

  get id(): string {
    return this._props.id;
  }
  get content(): string {
    return this._props.content;
  }
  get noteTime(): Date {
    return this._props.noteTime;
  }
  get authorId(): string | null {
    return this._props.authorId;
  }
  get isInternal(): boolean {
    return this._props.isInternal;
  }
  get noteCategory(): string | null {
    return this._props.noteCategory;
  }
  get noteMetadataJson(): string | null {
    return this._props.noteMetadataJson;
  }
  get isSystem(): boolean {
    return this._props.isSystem;
  }
  get sentToCards(): List<IdListItemProps> | null {
    return this._props.sentToCards;
  }
  get receivedFromCardId(): string | null {
    return this._props.receivedFromCardId;
  }
  get cardId(): string {
    return this._props.cardId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CardNoteProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        content: this._props.content,
        noteTime: this._props.noteTime,
        authorId: this._props.authorId,
        isInternal: this._props.isInternal,
        noteCategory: this._props.noteCategory,
        noteMetadataJson: this._props.noteMetadataJson,
        isSystem: this._props.isSystem,
        sentToCards: this._props.sentToCards,
        receivedFromCardId: this._props.receivedFromCardId,
        cardId: this._props.cardId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCardNoteEntities = (state: AppState) => state.entityStores.cardNote.entities;

export const selectCardNoteById = (id: string) => (state: AppState) =>
  state.entityStores.cardNote.entities.get(id);

export const selectCardNoteMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cardNote.meta.get(id);

export const selectCardNoteAndMetadataById = (id: string) =>
  createSelector(selectCardNoteById(id), selectCardNoteMetadataById(id), (cardNote, meta) => ({
    cardNote,
    meta,
  }));

export function selectCardNoteEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCardNoteEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CardNoteRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCardNoteEntitiesByContent(content: string) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectCardNoteEntitiesByNoteTime(noteTime: Date) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.noteTime === noteTime).valueSeq(),
  );
}

export function selectCardNoteEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectCardNoteEntitiesByIsInternal(isInternal: boolean) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.isInternal === isInternal).valueSeq(),
  );
}

export function selectCardNoteEntitiesByNoteCategory(noteCategory: string) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.noteCategory === noteCategory).valueSeq(),
  );
}

export function selectCardNoteEntitiesByNoteMetadataJson(noteMetadataJson: string) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.noteMetadataJson === noteMetadataJson).valueSeq(),
  );
}

export function selectCardNoteEntitiesByIsSystem(isSystem: boolean) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.isSystem === isSystem).valueSeq(),
  );
}

export function selectCardNoteEntitiesByReceivedFromCardId(receivedFromCardId: string) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.receivedFromCardId === receivedFromCardId).valueSeq(),
  );
}

export function selectCardNoteEntitiesByCardId(cardId: string) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}

export function selectCardNoteEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllCardNoteEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
